import React, { useState, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Upload, FileIcon, X, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { getFileCategory, formatFileSize } from '../mockData';
import imageCompression from 'browser-image-compression';

const FILE_SIZE_LIMITS = {
  'image/jpeg': 1.5 * 1024 * 1024,
  'image/png': 1.5 * 1024 * 1024,
  'image/svg+xml': 1.5 * 1024 * 1024,
  'image/webp': 1.5 * 1024 * 1024,
  'application/pdf': 10 * 1024 * 1024,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 10 * 1024 * 1024,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 10 * 1024 * 1024,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 10 * 1024 * 1024
};

// Admin can upload up to 100MB for any file type
const ADMIN_FILE_SIZE_LIMIT = 100 * 1024 * 1024;

const ALLOWED_TYPES = Object.keys(FILE_SIZE_LIMITS);

const FileUploadCard = ({ onFileUpload, directories, user }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isPublic, setIsPublic] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false); // Default to false for security
  const [allowDelete, setAllowDelete] = useState(false);
  const [autoDeletePeriod, setAutoDeletePeriod] = useState('never');
  const [customDirectory, setCustomDirectory] = useState('auto');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressImages, setCompressImages] = useState(false); // Default to false
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  // Auto-toggle allowDownload based on visibility
  React.useEffect(() => {
    if (isPublic) {
      setAllowDownload(true);
    } else {
      setAllowDownload(false);
    }
  }, [isPublic]);

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Only images (JPEG, PNG, SVG, WebP), PDFs, and documents (Word, Excel, PowerPoint) are allowed.',
        variant: 'destructive'
      });
      return false;
    }

    // Admins get 100MB limit, regular users get type-specific limits
    const maxSize = user?.is_admin ? ADMIN_FILE_SIZE_LIMIT : FILE_SIZE_LIMITS[file.type];
    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: `File size exceeds the limit of ${formatFileSize(maxSize)}.`,
        variant: 'destructive'
      });
      return false;
    }

    return true;
  };

  const handleFileSelect = async (file) => {
    // Check if file type is allowed
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Only images (JPEG, PNG, SVG, WebP), PDFs, and documents are allowed.',
        variant: 'destructive'
      });
      return;
    }

    // For images, try to compress them first (if enabled)
    if (compressImages && file.type.startsWith('image/') && file.type !== 'image/svg+xml') {
      setCompressing(true);
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true
        };
        const compressedFile = await imageCompression(file, options);
        const finalFile = new File([compressedFile], file.name, { type: file.type });

        if (validateFile(finalFile)) {
          setSelectedFile(finalFile);
          toast({
            title: 'Image compressed',
            description: `Reduced from ${formatFileSize(file.size)} to ${formatFileSize(finalFile.size)}`
          });
        }
      } catch (error) {
        console.error('Compression failed:', error);
        // Fallback: try original file
        if (validateFile(file)) {
          setSelectedFile(file);
        }
      } finally {
        setCompressing(false);
      }
    } else {
      // Non-image files: validate directly
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Import API
      const { filesApi } = await import('../lib/api');

      // Calculate expiry in seconds
      const expiresIn = autoDeletePeriod === 'never'
        ? null
        : parseInt(autoDeletePeriod) * 24 * 60 * 60;

      setUploadProgress(20);

      // Start upload
      const { id } = await filesApi.startUpload(selectedFile, {
        isPublic,
        allowDownload,
        allowDelete,
        expiresIn,
        directoryId: customDirectory === 'auto' ? null : customDirectory
      });

      setUploadProgress(50);

      // Upload file content
      await filesApi.uploadChunk(id, selectedFile, 0);

      setUploadProgress(80);

      // Complete upload
      await filesApi.completeUpload(id);

      setUploadProgress(100);

      // Create file object for UI
      const newFile = {
        id,
        name: selectedFile.name,
        size: selectedFile.size,
        contentType: selectedFile.type,
        isPublic,
        directoryId: customDirectory === 'auto' ? getFileCategory(selectedFile.type) : customDirectory,
        userId: user?.id,
        uploaderName: user?.name,
        uploaderUsername: user?.username,
        uploadedBy: 'user',
        uploadedAt: new Date().toISOString(),
        expiresAt: autoDeletePeriod === 'never' ? null : new Date(Date.now() + parseInt(autoDeletePeriod) * 24 * 60 * 60 * 1000).toISOString(),
        autoDeleteIn: autoDeletePeriod === 'never' ? 'never' : `${autoDeletePeriod} days`,
        url: selectedFile.type.startsWith('image/') ? URL.createObjectURL(selectedFile) : null,
        allowDownload,
        allowDelete,
        accessStatus: null,
        isLocked: false
      };

      onFileUpload(newFile);

      toast({
        title: 'File uploaded successfully',
        description: `${selectedFile.name} has been uploaded to the server.`
      });

      setSelectedFile(null);
      setAutoDeletePeriod('never');
      setCustomDirectory('auto');
    } catch (error) {
      console.error('Upload failed:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload file. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload File</h2>

      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-teal-400'
          }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {!selectedFile ? (
          <>
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-2">Drag and drop your file here</p>
            <p className="text-sm text-gray-500 mb-4">or</p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="border-teal-500 text-teal-700 hover:bg-teal-50"
            >
              Browse Files
            </Button>
            <Input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".jpg,.jpeg,.png,.svg,.webp,.pdf,.docx,.xlsx,.pptx"
              onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            />
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start text-left">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-xs text-blue-800">
                  <p className="font-medium mb-1">File size limits:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Images (JPEG, PNG, SVG, WebP): Max 1.5 MB</li>
                    <li>PDFs: Max 10 MB</li>
                    <li>Documents (Word, Excel, PowerPoint): Max 10 MB</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <FileIcon className="h-8 w-8 text-teal-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedFile(null)}
                disabled={uploading}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {uploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-teal-500 to-cyan-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {!uploading && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <div className="space-y-2">
                  <Label htmlFor="directory">Directory</Label>
                  <Select value={customDirectory} onValueChange={setCustomDirectory}>
                    <SelectTrigger id="directory">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-categorize</SelectItem>
                      {directories.map(dir => (
                        <SelectItem key={dir.id} value={dir.id}>{dir.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="autoDelete">Auto-delete after</Label>
                  <Select value={autoDeletePeriod} onValueChange={setAutoDeletePeriod}>
                    <SelectTrigger id="autoDelete">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">Never</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="visibility"
                    checked={isPublic}
                    onCheckedChange={setIsPublic}
                    disabled={!user?.is_admin && isPublic}
                  />
                  <Label htmlFor="visibility" className="cursor-pointer">
                    {isPublic ? 'Public' : 'Private'} file
                    {!user?.is_admin && isPublic && (
                      <span className="ml-2 text-[10px] text-amber-600 font-normal">(Admin required to revert to Private)</span>
                    )}
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="compress"
                    checked={compressImages}
                    onCheckedChange={setCompressImages}
                  />
                  <Label htmlFor="compress" className="cursor-pointer">
                    {compressImages ? 'Compress images' : 'Original quality'}
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="allowDownload"
                    checked={allowDownload}
                    onCheckedChange={setAllowDownload}
                  />
                  <Label htmlFor="allowDownload" className="cursor-pointer">
                    Allow others to Download
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="allowDelete"
                    checked={allowDelete}
                    onCheckedChange={setAllowDelete}
                  />
                  <Label htmlFor="allowDelete" className="cursor-pointer text-red-600">
                    Allow others to Delete
                  </Label>
                </div>
              </div>
            )}

            {!uploading && (
              <Button
                onClick={handleUpload}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
              >
                Upload File
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default FileUploadCard;
